/**
 * @module appSlice
 * 
 * Redux slice for managing the state of admin applications.
 * It handles actions such as setting, resetting, and updating apps, as well as fetching apps from the service.
 * 
 * @alpha
 * This module is part of the alpha version and may change in the future.
 */

import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { IAdminApp } from 'src/services/IAM/AdminApps/AdminApp.interface'
import {
  getApps,
  deleteApp,
  updateApp,
} from 'src/services/IAM/AdminApps/AdminApp.service'
import { IAdminAppState } from './adminApps.interface'

/**
 * Initial state for the admin apps slice.
 * 
 * @defaultValue
 * ```typescript
 * {
 *   app: {
 *     apiGatewayId: '',
 *     name: '',
 *     type: '',
 *     description: '',
 *   },
 *   appUpdated: {
 *     apiGatewayId: '',
 *     name: '',
 *     type: '',
 *     description: '',
 *   },
 *   apps: [],
 *   loading: false,
 *   error: null,
 * }
 * ```
 */
const initialState: IAdminAppState = {
  app: {
    apiGatewayId: '',
    name: '',
    type: '',
    description: '',
  },
  appUpdated: {
    apiGatewayId: '',
    name: '',
    type: '',
    description: '',
  },
  apps: [],
  loading: false,
  error: null,
}

/**
 * Slice for managing admin apps state in Redux.
 * 
 * @remarks
 * This slice handles the following actions:
 * - Setting and resetting the current app
 * - Setting and resetting the updated app
 * - Fetching apps from the server
 * - Handling loading and error states
 * 
 * @category Redux Slice
 */
const appSlice = createSlice({
  name: 'app',
  initialState,
  reducers: {
    /**
     * Sets the current app state.
     * 
     * @param state - The current state of the app slice
     * @param action - The action payload containing the app data
     * @returns void
     * 
     * @example
     * dispatch(setApp({ apiGatewayId: '123', name: 'New App', type: 'web', description: 'Test app' }))
     */
    setApp: (state, action: PayloadAction<IAdminApp>) => {
      state.app = action.payload
    },

    /**
     * Sets the updated app state.
     * 
     * @param state - The current state of the app slice
     * @param action - The action payload containing the updated app data
     * @returns void
     * 
     * @example
     * dispatch(setAppUpdated({ apiGatewayId: '123', name: 'Updated App', type: 'mobile', description: 'Updated description' }))
     */
    setAppUpdated: (state, action: PayloadAction<IAdminApp>) => {
      state.appUpdated = action.payload
    },

    /**
     * Resets the app state to its initial value.
     * 
     * @returns The initial state
     * 
     * @example
     * dispatch(resetApp())
     */
    resetApp: () => initialState,

    /**
     * Resets the list of apps to an empty array.
     * 
     * @param state - The current state of the app slice
     * @returns void
     * 
     * @example
     * dispatch(resetApps())
     */
    resetApps: (state) => {
      state.apps = []
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(
        getApps.fulfilled,
        (state, action: PayloadAction<IAdminApp[]>) => {
          state.loading = false
          state.apps = action.payload
        },
      )
      .addCase(getApps.rejected, (state, action: PayloadAction<any>) => {
        state.loading = false
        state.error = action.payload
      })
      .addCase(deleteApp.pending, (state, action: PayloadAction<any>) => {
        state.loading = false
      })
      .addCase(deleteApp.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false
      })
      .addCase(deleteApp.rejected, (state, action: PayloadAction<any>) => {
        state.loading = false
        state.error = action.payload
      })
      .addCase(updateApp.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false
      })
      .addCase(updateApp.rejected, (state, action: PayloadAction<any>) => {
        state.loading = false
        state.error = action.payload
      })
  },
})

/**
 * Action creators generated from the app slice reducers.
 * 
 * @see {@link setApp}
 * @see {@link setAppUpdated}
 * @see {@link resetApp}
 * @see {@link resetApps}
 */
export const { resetApp, setApp, resetApps, setAppUpdated } = appSlice.actions

/**
 * Default reducer export for the app slice.
 * 
 * @public
 */
export default appSlice.reducer
